<template>
  <v-app>
    <v-app-bar app color="primary" clipped-left dark>
      <v-menu
        ref="menu"
        v-model="isMenuOpened"
        v-if="isUserLoggedIn"
        offset-y
        transition="slide-x-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn text icon large v-on="on" class="hamburger-menu">
            <v-icon x-large>menu</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            v-for="(item, index) in availableMenuItems"
            :key="index"
            @click="item.click($event, item)"
          >
            <v-list-item-content>
              <v-menu
                open-on-hover
                v-if="item.subItems && item.subItems.length > 0"
                offset-x
                transition="slide-x-transition"
              >
                <template v-slot:activator="{ on }">
                  <v-list-item-title v-on="on">
                    {{ item.title }}
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-list-item-title>
                  <v-list-item-subtitle v-on="on">{{ item.hint }}</v-list-item-subtitle>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(item, index) in item.subItems"
                    :key="index"
                    @click="item.click($event, item)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.hint }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>

              <v-list-item-title v-if="!item.subItems || item.subItems.length === 0">{{
                item.title
              }}</v-list-item-title>
              <v-list-item-subtitle v-if="!item.subItems || item.subItems.length === 0">{{
                item.hint
              }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <h1 class="title">{{ $t('CONTAIN-MAP-HU') }}</h1>
      <v-spacer />
      <LanguageSelector />
      <v-menu offset-y v-if="isUserLoggedIn">
        <template v-slot:activator="{ on }">
          <v-btn dark icon v-on="on">
            <v-icon>account_circle</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title>{{ displayName }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="logout">
            <v-list-item-title>{{ $t('Logout') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <v-content>
      <router-view></router-view>
    </v-content>

    <v-footer app dark color="primary">
      <div style="font-size:14px;" v-if="Object.keys(peopleStats).length">
        <span class="pa-1">{{ $t('Stats') }} ({{ statsDate }})</span>
        <span class="pa-2" v-for="(status, index) in translatedStatuses" :key="index"
          >{{ status.label }}: {{ peopleStats[status.value] ? peopleStats[status.value] : 0 }}</span
        >
      </div>
      <v-spacer />
      <v-card class="pl-2 pr-2 pb-0 mr-6" color="info">
        <v-icon small class="mr-1" color="secondary">mdi-information</v-icon>
        <span class="caption font-weight-bold secondary--text">{{
          $t('The site does not store any kind of personal data.')
        }}</span>
      </v-card>

      <span class="caption font-weight-light">{{ startupVersion | versionFormat }}</span>
    </v-footer>
    <v-snackbar v-model="newReleaseAvailable" :bottom="true" :absolute="true" :timeout="10000">
      <v-icon>autorenew</v-icon>
      {{ $t('New version available.') }}
      {{ $t('Please reload the page.') }}
      <v-btn @click="forceReload">{{ $t('Reload') }}</v-btn>
    </v-snackbar>
    <v-snackbar id="requestInProgress" v-model="requestInProgress" :bottom="true" :timeout="0">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      {{ $t('Loading is in progress...') }}
    </v-snackbar>
  </v-app>
</template>

<style lang="sass">
@import "./App.scss"
</style>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import ApiService from './services/ApiService';
import LanguageSelector from './components/LanguageSelector';
import { DATA_TRANSLATIONS } from './helpers/dataTranslations';

export default {
  name: 'app',

  data() {
    return {
      env: process.env.NODE_ENV,
      startupVersion: undefined,
      newReleaseAvailable: false,
      polling: undefined,
      isMenuOpened: false,
      peopleStats: {},
      statsDate: undefined,
      statuses: [],
      extraStatuses: [
        {
          value: 'osszesen',
          label: 'összesen'
        }
      ]
    };
  },

  created() {
    if (this.$store.state.account.status.loggedIn) {
      this.$store.dispatch('app/getConfig');
    }
    // set startup version
    this.$store.dispatch('app/fetchVersion');
    // start delayed version polling
    this.pollVersion();
  },

  mounted() {
    if (this.$store.state.account.status.loggedIn) {
      if (this.config) {
        this.statuses = [...this.extraStatuses, ...this.config.statuses.filter((a) => a.showOnMap)];
      }
      this.todayStats();
    }
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  computed: {
    menuItems() {
      return [
        {
          title: this.$t('Map view'),
          click: () => this.navigateTo('/')
        },
        {
          title: this.$t('List view'),
          click: () => this.navigateTo('/list')
        },
        {
          title: this.$t('Summary by areas'),
          click: () => this.navigateTo('/area-dashboard')
        },
        {
          title: this.$t('Graphs'),
          click: (event) => {
            event.stopPropagation();
            return false;
          },
          subItems: [
            { title: this.$t('Epidemic curve'), click: () => this.navigateTo('/chart') },
            { title: this.$t('Age pyramid'), click: () => this.navigateTo('/chartAges') }
          ]
        },
        {
          title: this.$t('Thematic map'),
          click: () => this.navigateTo('/thematicMap')
        },
        {
          title: this.$t('Home quarantine application map'),
          click: () => this.navigateTo('/asuraMap')
        },
        {
          title: this.$t('Mobile tracking'),
          click: () => this.navigateTo('/mobileTracking')
        },
        {
          title: this.$t('Institution datas'),
          click: () => this.navigateTo('/extraLayers')
        },
        {
          title: this.$t('Import'),
          available: () => this.isAdmin || this.isEditor,
          click: () => this.navigateTo('/import')
        },
        {
          title: this.$t('Users'),
          available: () => this.isAdmin,
          click: () => this.navigateTo('/userlist')
        }
      ];
    },

    availableMenuItems() {
      return this.menuItems.filter((item) => item.available === undefined || item.available());
    },

    ...mapState({
      alert: (state) => state.alert,
      account: (state) => state.account,
      version: (state) => state.app.version,
      config: (state) => state.app.config
    }),
    ...mapGetters('account', ['isUserLoggedIn', 'isAdmin', 'isEditor', 'displayName']),
    ...mapGetters('app', ['requestInProgress']),
    selectedDate() {
      return this.$store.state.map.date;
    },
    translatedStatuses() {
      return this.statuses.map((x) => {
        return { ...x, label: this.$t(DATA_TRANSLATIONS[x.value]) };
      });
    }
  },
  filters: {
    versionFormat: (v) => {
      if (v && v.buildVersion && v.commitHash) {
        return 'v:r' + v.buildVersion + '#' + v.commitHash.substring(0, 8);
      } else {
        return '';
      }
    }
  },
  methods: {
    pollVersion() {
      this.polling = setInterval(() => {
        this.$store.dispatch('app/fetchVersion');
      }, 60000); // every minute
    },
    forceReload() {
      this.startupVersion = undefined;
      this.newReleaseAvailable = false;
      document.location.reload(true);
    },
    navigateTo(path) {
      if (this.$router.currentRoute.path !== path) {
        this.isMenuOpened = false;
        this.$router.push(path);
        this.todayStats();
      }
    },
    ...mapActions({
      clearAlert: 'alert/clear',
      logout: 'account/logout'
    }),
    todayStats() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      this.statsDate = today.toLocaleDateString('hu-HU');
      ApiService.getPeopleStats(today).then((response) => {
        this.peopleStats = response.data;
      });
    }
  },
  watch: {
    alert: {
      handler(val) {
        if (val.message) {
          this.$swal(this.$t(val.message.toString()));
          this.clearAlert();
        }
      },
      deep: true
    },
    account: {
      handler(val) {
        if (!val.status.isLoggedIn) {
          this.peopleStats = {};
        }
      },
      deep: true
    },
    selectedDate(date) {
      this.statsDate = date.toLocaleDateString('hu-HU');
      ApiService.getPeopleStats(date).then((response) => {
        this.peopleStats = response.data;
      });
    },
    config(config) {
      if (config) {
        this.statuses = [...this.extraStatuses, ...config.statuses.filter((a) => a.showOnMap)];
      }
    },
    version(version) {
      if (!this.startupVersion && version) {
        this.startupVersion = version;
      }
      this.newReleaseAvailable |=
        version &&
        this.startupVersion &&
        JSON.stringify(version) != JSON.stringify(this.startupVersion);
    },
    isUserLoggedIn(val) {
      if (val) {
        this.$store.dispatch('app/getConfig');
      }
    }
  },
  components: { LanguageSelector }
};
</script>
